import { useEffect } from 'react';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';

const sidingTypes = [
  {
    icon: 'ri-home-2-line',
    title: 'Vinyl Siding',
    description: 'Durable, low-maintenance, and cost-effective. Available in a wide range of colors and styles to match any architectural design.'
  },
  {
    icon: 'ri-building-2-line',
    title: 'Fiber Cement',
    description: 'Premium durability with superior fire and weather resistance. Mimics the look of wood without the maintenance requirements.'
  },
  {
    icon: 'ri-plant-line',
    title: 'Cedar Siding',
    description: 'Natural beauty with timeless appeal. Provides excellent insulation and ages gracefully with proper maintenance.'
  }
];

const benefits = [
  {
    icon: 'ri-shield-check-line',
    title: 'Weather Protection',
    description: 'Superior protection against rain, wind, and extreme temperatures to keep your property safe and dry.'
  },
  {
    icon: 'ri-money-dollar-circle-line',
    title: 'Energy Efficiency',
    description: 'Improved insulation reduces heating and cooling costs, saving you money year-round.'
  },
  {
    icon: 'ri-palette-line',
    title: 'Enhanced Curb Appeal',
    description: 'Transform your property\'s appearance with beautiful, modern siding that increases value.'
  },
  {
    icon: 'ri-time-line',
    title: 'Long-Lasting',
    description: 'Quality materials and expert installation ensure your siding lasts for decades with minimal maintenance.'
  }
];

const processSteps = [
  {
    number: '01',
    title: 'Consultation & Assessment',
    description: 'We evaluate your property, discuss material options, and provide a detailed estimate.'
  },
  {
    number: '02',
    title: 'Material Selection',
    description: 'Choose from our wide range of siding materials, colors, and styles to match your vision.'
  },
  {
    number: '03',
    title: 'Preparation',
    description: 'We remove old siding, repair any underlying damage, and prepare the surface for installation.'
  },
  {
    number: '04',
    title: 'Professional Installation',
    description: 'Our expert team installs your new siding with precision, ensuring proper weatherproofing.'
  },
  {
    number: '05',
    title: 'Final Inspection',
    description: 'Thorough quality check and walkthrough to ensure your complete satisfaction.'
  }
];

export default function SidingPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-amber-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="mb-4">
                <span className="text-brand text-sm font-semibold uppercase tracking-wider">
                  Professional Siding Services
                </span>
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                Transform Your Property with Expert Siding
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Enhance your property's curb appeal, energy efficiency, and weather protection with our professional siding installation and replacement services. We offer a wide range of materials to suit any style and budget.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="#contact"
                  className="px-8 py-4 bg-brand text-white font-semibold rounded-lg hover:bg-brand-dark transition-colors shadow-lg hover:shadow-xl whitespace-nowrap"
                  onClick={(e) => {
                    e.preventDefault();
                    window.location.href = '/#contact';
                  }}
                >
                  Request Free Estimate
                </a>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://public.readdy.ai/ai/img_res/3bb8dade3d44067d8e70fa37dc8753aa.jpg"
                alt="Professional Siding Installation"
                className="rounded-2xl shadow-2xl w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Siding Types */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Siding Materials We Offer
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Choose from our premium selection of siding materials, each offering unique benefits and aesthetic appeal
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {sidingTypes.map((type, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-brand/10 to-white rounded-2xl p-10 border-2 border-brand/20 hover:border-brand/50 transition-all duration-300 hover:shadow-lg"
              >
                <div className="w-16 h-16 flex items-center justify-center mb-6">
                  <i className={`${type.icon} text-5xl text-brand`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {type.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {type.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Why Choose Professional Siding?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Quality siding installation provides lasting benefits for your property
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300"
              >
                <div className="w-14 h-14 flex items-center justify-center mb-6 bg-brand/10 rounded-xl">
                  <i className={`${benefit.icon} text-3xl text-brand`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Our Installation Process
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              A proven workflow that ensures quality results and your complete satisfaction
            </p>
          </div>

          <div className="grid md:grid-cols-5 gap-6">
            {processSteps.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-gradient-to-br from-brand/10 to-white rounded-2xl p-6 border-2 border-brand/20 h-full">
                  <div className="text-5xl font-bold text-brand mb-4 opacity-30">
                    {step.number}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {step.description}
                  </p>
                </div>
                {index < processSteps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                    <i className="ri-arrow-right-line text-2xl text-brand"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden" style={{backgroundImage: 'url(https://readdy.ai/api/search-image?query=professional%20siding%20installation%20workers%20installing%20vinyl%20siding%20on%20modern%20residential%20home%20exterior%20construction%20site%20bright%20daylight%20professional%20photography%20high%20quality&width=1600&height=700&seq=ctasiding1&orientation=landscape)', backgroundSize: 'cover', backgroundPosition: 'center'}}>
        <div className="absolute inset-0 bg-black/55"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Transform Your Property?
          </h2>
          <p className="text-xl text-white/80 mb-10 leading-relaxed">
            Get a free, no-obligation estimate for your siding project. Our experts are ready to help you choose the perfect materials and bring your vision to life.
          </p>
          <a
            href="/#contact"
            className="inline-block px-10 py-5 bg-white text-brand font-bold rounded-lg hover:bg-gray-50 transition-colors shadow-xl hover:shadow-2xl text-lg whitespace-nowrap"
          >
            Request Your Free Estimate
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}